<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import LazyVideo from './LazyVideo.vue'

interface StepItem {
    title: string
    desc: string
}

const props = defineProps({
    title: {
        type: String,
        default: 'HOW TO USE',
    },
    desc: {
        type: String,
        default: '',
    },
    steps: {
        type: Array as PropType<StepItem[]>,
        default: () => [],
    },
    img: {
        type: String,
        default: '',
    },
    btnInfo: {
        type: String,
        default: '',
    },
})

// 判断 img 是否是视频
const isVideo = computed(() =>
    props.img ? /\.(mp4|webm|ogg)(\?.*)?$/i.test(props.img) : false
)

// 点击按钮，回到顶部
const returnTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    })
}
</script>

<template>
    <!-- How to Use Section -->
    <section class="how-to-use-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
                <p class="section-subtitle">{{ desc }}</p>
            </div>

            <div class="steps-container" v-if="!img">
                <div
                    class="step-item"
                    v-for="(step, index) in steps"
                    :key="index"
                >
                    <div class="step-number">{{ index + 1 }}</div>
                    <h3 class="step-title">{{ step.title }}</h3>
                    <p class="step-description" v-html="step.desc"></p>
                </div>
            </div>
            <div v-else class="hasImg-container">
                <LazyVideo
                    v-if="isVideo"
                    :src="img"
                />
                <img v-else :src="img" :alt="title || 'How to use video upscaler guide'" loading="lazy" />
                <div class="steps">
                    <div
                        class="step-item"
                        v-for="(step, index) in steps"
                        :key="index"
                    >
                        <h3 class="step-title">{{ step.title }}</h3>
                        <p class="step-description">{{ step.desc }}</p>
                    </div>
                </div>
            </div>

            <div v-if="btnInfo" class="btnInfo" @click="returnTop">
                {{ btnInfo }}
            </div>
        </div>
    </section>
</template>

<style scoped lang="less">
.section-header {
    text-align: center;
    margin-bottom: 3rem;

    .section-subtitle {
        color: #adadad;
        font-size: 1rem;
        font-weight: 400;
    }

    @media (max-width: 768px) {
        margin-bottom: 2rem;
        padding: 0 16px;

        .section-subtitle {
            font-size: 0.875rem;
        }
    }

    @media (max-width: 480px) {
        margin-bottom: 1.5rem;

        .section-subtitle {
            font-size: 0.8rem;
        }
    }
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2rem;
        margin-bottom: 0.75rem;
    }

    @media (max-width: 480px) {
        font-size: 1.5rem;
        margin-bottom: 0.5rem;
    }
}

.how-to-use-section {
    background: #120e11;
    margin-bottom: 240px;
    padding-bottom: 0;
    padding-top: 11.25rem;

    .container {
        margin: 0 auto;
    }

    @media (max-width: 768px) {
        margin-bottom: 120px;
        padding-top: 5rem;
    }

    @media (max-width: 480px) {
        margin-bottom: 80px;
        padding-top: 3rem;
    }
}

.steps-container {
    display: flex;
    justify-content: space-between;
    position: relative;
    gap: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    padding: 5rem;
    border-radius: 8px;
    overflow: hidden;

    @media (max-width: 1024px) {
        flex-direction: column;
        padding: 3rem 2rem;
        gap: 1.5rem;
    }

    @media (max-width: 768px) {
        padding: 2rem 1.5rem;
        gap: 1.25rem;
        border-radius: 12px;
    }

    @media (max-width: 480px) {
        padding: 1.5rem 1rem;
        gap: 1rem;
    }
}

.steps-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('https://static.weryai.com/growth/image/28cd14ee5659acb6b48ff315a8709737.webp'); /* 你的背景图路径 */
    background-size: cover;
    background-position: center;
    z-index: 1;
}

.steps-container::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        to bottom,
        rgba(18, 14, 17, 1) 0%,
        rgba(18, 14, 17, 1) 10%,
        rgba(18, 14, 17, 0.7) 30%,
        rgba(18, 14, 17, 0.7) 50%,
        rgba(18, 14, 17, 0.3) 100%
    );
    z-index: 2;
}

.step-item {
    flex: 1;
    text-align: left;
    backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 1rem 2rem;
    position: relative;
    z-index: 4;

    @media (max-width: 768px) {
        padding: 1rem 1.5rem;
    }

    @media (max-width: 480px) {
        padding: 0.875rem 1rem;
    }
}

.step-number {
    width: 3rem;
    height: 3rem;
    position: relative;
    background-color: #252527;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    margin: 0 auto 0.75rem;
    margin-left: 5px;

    @media (max-width: 768px) {
        width: 2.5rem;
        height: 2.5rem;
        font-size: 1.25rem;
        margin-bottom: 0.5rem;
    }

    @media (max-width: 480px) {
        width: 2rem;
        height: 2rem;
        font-size: 1rem;
    }
}

.step-number::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    box-shadow: 4px 4px 6px rgba(255, 255, 255, 0.4) inset;
    z-index: -1;
    border-top: 1px solid rgba(255, 255, 255, 0.8);

    @media (max-width: 768px) {
        top: -3px;
        left: -3px;
        right: -3px;
        bottom: -3px;
    }
}

.step-title {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: white;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        margin-bottom: 0.375rem;
    }

    @media (max-width: 480px) {
        font-size: 0.85rem;
    }
}

.step-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }

    @media (max-width: 480px) {
        font-size: 0.75rem;
    }
}

.btnInfo {
    border: 1px solid #98f668;
    padding: 10px 0;
    text-align: center;
    border-radius: 10px;
    cursor: pointer;
    width: 500px;
    margin: 30px auto;
    margin-top: 10px;
    margin-bottom: 0;
    color: #ffffff;

    @media (max-width: 768px) {
        width: 90%;
        max-width: 400px;
        padding: 12px 0;
        font-size: 0.875rem;
        margin: 20px auto;
        margin-bottom: 0;
    }

    @media (max-width: 480px) {
        width: 100%;
        padding: 10px 0;
        font-size: 0.8rem;
    }
}

.hasImg-container {
    display: flex;
    column-gap: 20px;
    justify-content: center;
    align-items: center;

    img,
    video {
        max-width: 800px;
        max-height: 500px;
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 16px;
    }

    .steps {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
        justify-content: space-around;

        .step-item {
            flex: unset;
        }
    }

    @media (max-width: 1024px) {
        flex-direction: column;
        row-gap: 20px;

        img,
        video {
            width: 100%;
            height: auto;
        }

        .steps {
            row-gap: 15px;
        }
    }

    @media (max-width: 768px) {
        img,
        video {
            border-radius: 12px;
        }
    }
}
</style>
